import React, { useEffect, useState } from "react";
import { memberApi } from "@/lib/memberAuth";
import { MapPin, Globe, MagnifyingGlass, EnvelopeSimple, ArrowUpRight } from "@phosphor-icons/react";

export default function MemberDirectory() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    memberApi
      .get("/member/directory", { params: q ? { q } : {} })
      .then(({ data }) => setMembers(data.members || []))
      .finally(() => setLoading(false));
  }, [q]);

  return (
    <div data-testid="portal-directory-page">
      <div className="mb-6">
        <div className="overline mb-2">Directory</div>
        <h1 className="font-display text-3xl font-bold text-navy tracking-tight">Member directory</h1>
        <p className="text-navy/70 mt-1 text-sm">Every member is territory-vetted and reviewed by the operator. Contact details are private to the network.</p>
      </div>

      <div className="relative mb-6">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
        <input
          data-testid="portal-directory-search"
          placeholder="Search by company, country, city or trade-lane…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full border border-black/15 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
        />
      </div>

      {loading ? (
        <div className="text-navy/60" data-testid="portal-directory-loading">Loading…</div>
      ) : members.length === 0 ? (
        <div className="border border-dashed border-black/15 p-10 text-center text-navy/60" data-testid="portal-directory-empty">
          No members match this search yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="portal-directory-grid">
          {members.map((m) => (
            <button
              key={m.id}
              onClick={() => setSelected(m)}
              data-testid={`portal-member-${m.id}`}
              className="text-left bg-white border border-black/10 p-5 hover:border-navy transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="overline">{m.membership_type?.replace("_", " ") || "member"}</span>
                <Globe size={16} weight="duotone" className="text-ember" />
              </div>
              <div className="font-display font-bold text-lg text-navy leading-tight flex items-center gap-2">
                {m.company_name}
                {m.is_founding_partner && (
                  <span className="text-[9px] uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5" data-testid={`founding-badge-${m.id}`}>Founding · {m.founding_year || "2026"}</span>
                )}
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-xs text-navy/60">
                <MapPin size={12} /> {m.city ? `${m.city}, ` : ""}{m.country}
              </div>
              <p className="mt-3 text-sm text-navy/70 line-clamp-2">{m.about}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                {(m.services || []).slice(0, 4).map((s) => (
                  <span key={s} className="text-[10px] uppercase tracking-widest border border-navy/20 px-1.5 py-0.5 text-navy/70">{s}</span>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" onClick={() => setSelected(null)}>
          <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-black/10 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="portal-member-detail-modal">
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
              <div>
                <div className="overline">{selected.membership_type?.replace("_", " ") || "member"}</div>
                <div className="font-display font-bold text-2xl text-navy mt-1">{selected.company_name}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-navy/50 hover:text-navy text-2xl leading-none" data-testid="portal-member-detail-close">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-navy/70 flex items-center gap-2"><MapPin size={14} /> {selected.city ? `${selected.city}, ` : ""}{selected.country}</div>
              {selected.about && <p className="text-navy/80 text-sm leading-relaxed">{selected.about}</p>}
              {selected.trade_lanes && (
                <div>
                  <div className="overline mb-1">Trade lanes</div>
                  <div className="text-sm text-navy/80">{selected.trade_lanes}</div>
                </div>
              )}
              {(selected.services || []).length > 0 && (
                <div>
                  <div className="overline mb-1">Services</div>
                  <div className="flex flex-wrap gap-1">
                    {(selected.services || []).map((s) => (
                      <span key={s} className="text-[10px] uppercase tracking-widest border border-navy/20 px-1.5 py-0.5 text-navy/70">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-black/10 pt-4 space-y-1.5 text-sm">
                {selected.contact_email && (
                  <a href={`mailto:${selected.contact_email}`} className="flex items-center gap-2 text-navy hover:text-ember" data-testid="portal-member-detail-email">
                    <EnvelopeSimple size={14} /> {selected.contact_email}
                  </a>
                )}
                {selected.website && (
                  <a href={selected.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-navy hover:text-ember" data-testid="portal-member-detail-website">
                    <ArrowUpRight size={14} /> {selected.website.replace(/^https?:\/\//, "")}
                  </a>
                )}
              </div>
              <div className="text-[11px] text-navy/40 border-t border-black/5 pt-3">
                Directory data is confidential — do not share outside the EPAS member circle.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
